// 経営ダッシュボード（中身/TMS・Firestore連動）。trips×tachographs を月で集約し、全社KPI・売上/コスト構成・全社P&L・乗務員収支を表示。
// 集約は運行実績×収支と同じ computeOperations（6月＝ace_tms_pipelineと一致検証済み）。デモは同梱 sample.ops.json。
import { computeOperations, LABOR_RATE, VEHICLE_DAY_RATE, DIESEL_YEN_PER_L } from '../data/analytics/operations.js'
import { goalProgress, fmtGoalPct, goalBelow, todayIso } from '../data/goals.js'
import { COLLECTIONS, listDocs, getOne, upsert } from '../data/collections.js'
import { supabaseReady } from '../supabase.js'
import { resolveMonth, setSelectedMonth } from '../data/selectedMonth.js'
import sampleOps from './sample.ops.json'
import { allowSample, isDemoDataOn, saveDemoSetting, DEMO_TAG, noDataHtml } from '../data/demoData.js'
// 試算の燃費（車格別・2026-08-28）。前提パネルで設定し、受注試算の燃料費だけに効く。
// 実績側の燃費（下の kmL）とは役割が別＝KPI・収支の計算はこの値を一切見ない。
import { QUOTE_FUEL_DEFAULTS, setQuoteFuel } from '../data/quoteFuel.core.js'
import { refreshQuoteFuel } from '../data/quoteFuel.js'
// 自社情報（請求書の右上・振込先）。settings/company。請求書ビューが読む正本をここで編集する
import { getCompany, saveCompany, refreshCompany, COMPANY_FIELDS } from '../data/companyInfo.js'

const YEN = (n) => '¥' + Math.round(Number(n) || 0).toLocaleString('ja-JP')
// 金額入力のカンマ表示（2026-08-25 オーナー指示）。type=text＋inputmode=numeric で
// 表示は 95,000,000、読み取りはカンマを剥がして数値化。
const fmtNum = (x) => (x == null || x === '' ? '' : Math.round(Number(x)).toLocaleString('ja-JP'))
const parseNum = (v) => { const d = String(v == null ? '' : v).replace(/[^0-9]/g, ''); return d === '' ? null : Number(d) }
const MAN = (n) => (Math.round(Number(n) / 1e4)).toLocaleString('ja-JP') + '万'
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
const pad = (n) => String(n).padStart(2, '0')
function monthOf(o, kd) {
  const m = o && o.month != null ? String(o.month).trim() : ''
  if (/^\d{4}-\d{2}/.test(m)) return m.slice(0, 7)
  const iso = (o && (o.date || o.startAt || o.targetYm)) || (kd && o && o[kd])
  if (typeof iso === 'string' && /^\d{4}-\d{2}/.test(iso)) return iso.slice(0, 7)
  if (Array.isArray(iso) && iso[0] != null) {
    const y = (typeof o.date === 'string' && /^\d{4}/.test(o.date)) ? o.date.slice(0, 4) : String(new Date().getFullYear())
    return `${y}-${pad(iso[0])}`
  }
  return ''
}
const pct = (a, b) => (b ? Math.round(a / b * 1000) / 10 : 0)

const D = { el: null, ctx: {}, month: null, months: [], trips: [], tachos: [], sheets: [], online: false, fetchError: false, isSample: false, names: {}, assump: null, saved: null, open: false, saving: '', dispOpen: false, savingDisp: '', coOpen: false, savingCo: '' }

function styleOnce() {
  if (document.getElementById('db-style')) return
  const s = document.createElement('style'); s.id = 'db-style'
  s.textContent = `
  .db{display:flex;flex-direction:column;gap:13px}
  .db-bar{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
  .db-bar select{font-family:inherit;font-size:13px;padding:6px 10px;border:1px solid var(--e1);border-radius:8px;background:var(--card);color:var(--text)}
  .db-demo{font-size:11px;color:var(--signal);background:var(--card);border:1px solid var(--accent);border-radius:999px;padding:3px 10px}
  .db-open{margin-left:auto}
  .db .ct-grid{gap:13px}
  .db .ct-panel h3{margin:0 0 10px;font-size:12.5px;display:flex;align-items:center;gap:9px}
  .stack{display:flex;height:26px;border-radius:6px;overflow:hidden;margin-bottom:9px}
  .stack span{display:block;height:100%}
  .lg{display:flex;gap:14px;flex-wrap:wrap;font-size:11.5px;color:var(--text)}
  .lg i{display:inline-block;width:10px;height:10px;border-radius:3px;margin-right:5px;vertical-align:-1px}
  .wf{display:flex;flex-direction:column;gap:7px}
  .wfrow{display:grid;grid-template-columns:120px 1fr 108px;align-items:center;gap:9px;font-size:12px}
  .wftrack{position:relative;height:20px;background:var(--head);border-radius:5px}
  .wftrack>span{position:absolute;top:0;height:100%;border-radius:4px}
  .wfrow .amt{text-align:right;font-variant-numeric:tabular-nums;font-weight:600}
  .tr-row{display:flex;gap:10px;align-items:stretch}
  .tr-col{display:flex;flex-direction:column;align-items:center;gap:4px;flex:1;min-width:0}
  .tr-bars{display:flex;align-items:flex-end;gap:3px;height:150px}
  .tr-bars span{display:block;width:16px}
  .tr-m{font-size:10.5px}
  .tr-v{font-size:10px;color:var(--muted);font-variant-numeric:tabular-nums}
  .db-note{font-size:11px;color:var(--muted);line-height:1.6}
  .db-af{display:grid;grid-template-columns:repeat(auto-fill,minmax(158px,1fr));gap:10px}
  .db-af label{display:flex;flex-direction:column;gap:4px;font-size:10.5px;color:var(--text)}
  .db-af input{font-family:inherit;font-size:13px;padding:6px 8px;border:1px solid var(--e1);border-radius:7px;background:var(--head);color:var(--text);text-align:right;font-variant-numeric:tabular-nums}
  .db-af .unit{color:var(--text);font-size:9.5px}
  .db-acts{display:flex;gap:9px;align-items:center;margin-top:11px;flex-wrap:wrap}
  .db-dirty{font-size:10.5px;color:var(--signal);background:var(--head);border:1px solid var(--accent);border-radius:999px;padding:2px 9px}
  .db-savedok{font-size:10.5px;color:var(--pos);background:var(--head);border:1px solid var(--pos);border-radius:999px;padding:2px 9px}
  .db-set{display:inline-flex;align-items:center;gap:7px;font-size:12.5px;color:var(--text)}
  .db-set input{accent-color:var(--accent);width:15px;height:15px}
  `
  document.head.appendChild(s)
}

// デモ用: サンプル(2026-04 の1ヶ月・実CSV由来)を過去へ6ヶ月分複製する。乱数なし（決定的）。
// month プロパティを持たせ、年またぎ(2025-11〜)も monthOf が正しく解決する（month を最優先で読む）。
// 金額はスケールだけ変えて月ごとの高低を作る。本番(Supabase)データには一切触れない。
const DEMO_MONTHS = 6
const DEMO_SCALE = [1, 0.93, 1.06, 0.88, 0.97, 0.83] // 当月→過去
function expandDemo(src) {
  const trips = [], tachos = []
  for (let i = 0; i < DEMO_MONTHS; i++) {
    let y = 2026, m = 4 - i
    while (m < 1) { m += 12; y-- }
    const key = y + '-' + String(m).padStart(2, '0')
    const sc = DEMO_SCALE[i]
    for (const t of src.trips) {
      const c = { ...t, month: key }
      if (typeof c.fare === 'number') c.fare = Math.round(c.fare * sc)
      if (typeof c.charterCost === 'number') c.charterCost = Math.round(c.charterCost * sc)
      trips.push(c)
    }
    for (const t of src.tachographs) tachos.push({ ...t, month: key })
  }
  return { trips, tachos }
}

export async function renderDashboard(el, ctx = {}) {
  styleOnce()
  D.el = el
  D.ctx = ctx
  D.online = !ctx.demo && supabaseReady
  D.fetchError = false
  D.sheets = []
  D.names = {}
  el.innerHTML = '<div class="ct-state is-loading"><b class="ct-state__t">読み込んでいます…</b><span>選択月の運行データを集計しています</span></div>'
  let trips = sampleOps.trips, tachos = sampleOps.tachographs
  if (D.online) {
    try {
      const [tr, ta, dr, cs] = await Promise.all([listDocs(COLLECTIONS.trips), listDocs(COLLECTIONS.tachographs), listDocs(COLLECTIONS.drivers), listDocs(COLLECTIONS.chargeSheets)])
      D.sheets = cs || []
      if (tr && tr.length) { trips = tr; tachos = ta || []; D.names = {}; for (const d of (dr || [])) { const k = String(d.code || d.id || '').replace(/^0+(?=\d)/, ''); if (k) D.names[k] = d.name || '' } }
      // 実データ0件。サンプルへ落ちてよいかは共通判定 allowSample（設定 settings/display）に委ねる
      else if (!allowSample(D.online)) { trips = []; tachos = [] }
    } catch { D.online = false; D.fetchError = true }
  }
  // サインイン済みの実fetch失敗はサンプルへフォールバックせず is-error を主表示にする
  // （サンプル描画は明示的なデモ状況＝online初期値false のときだけ。2026-08-27 負債解消）
  if (D.fetchError) { D.trips = []; D.tachos = []; D.isSample = false; paintFetchError(); return }
  // サンプル運用時（明示的なデモ経路 or 実データ0件＋デモ表示ON）は6ヶ月分に複製して月次推移を見せる
  D.isSample = trips === sampleOps.trips
  // 実データ0件＋デモ表示OFF（既定）＝正直に空を出す（架空の実績を本物の顔で並べない。2026-08-27 案C）
  if (!D.isSample && !trips.length) { D.trips = []; D.tachos = []; paintNoData(); return }
  if (D.isSample) { const ex = expandDemo(sampleOps); trips = ex.trips; tachos = ex.tachos }
  D.trips = trips; D.tachos = tachos
  const months = new Set()
  for (const t of trips) { const m = monthOf(t, 'dm'); if (m) months.add(m) }
  for (const t of tachos) { const m = monthOf(t, 'sd'); if (m) months.add(m) }
  for (const h of D.sheets) { const m = monthOf(h); if (m) months.add(m) }
  const picked = resolveMonth([...months])
  D.months = picked.months
  D.month = picked.month
  // 人件費率は乗務員ごと＋試算用率に一本化（2026-08-27。#4→完結）。前提（試算）からは削除
  // kmL＝**実績**の燃費（デジタコ実測の上書き）。quoteKmL*＝**試算**の燃費（受注試算の燃料費・車格別）。
  // 別々に持つ理由は quoteFuel.core.js 冒頭。ここで混ぜない
  const def = { dayLarge: VEHICLE_DAY_RATE.large, dayMedium: VEHICLE_DAY_RATE.medium, dieselYen: DIESEL_YEN_PER_L, kmL: null,
    quoteKmLMedium: QUOTE_FUEL_DEFAULTS.medium, quoteKmLLarge: QUOTE_FUEL_DEFAULTS.large }
  let saved = null
  if (D.online) { try { saved = await getOne('settings', 'assumptions') } catch { saved = null } }
  if (saved && 'labor' in saved) delete saved.labor // 旧ドキュメントの残骸は無視（#4）
  D.saved = { ...def, ...(saved || {}) }
  setQuoteFuel(saved) // 取得済みの設定を試算側ストアへ（再取得しない。未設定・不正値は既定へ倒す）
  D.assump = { ...D.saved }
  D.goals = {}
  if (D.online) { try { D.goals = ((await getOne('settings', 'goals')) || {}).months || {} } catch { D.goals = {} } }
  // デモ用: 目標もサンプルで入れておく（オーナー指示 2026-08-25「目標も適当にいれておいて」）。
  // 実績が月ごとにスケールするため、固定の目標値で達成/未達が自然に混ざる。保存はされない。
  if (D.isSample) {
    const g = { uri: 90000000, shushi: 12000000, yo_arari: 1300000, jissharitsu: 90, akaji: 0 }
    for (const m of D.months) if (!D.goals[m]) D.goals[m] = { ...g }
  }
  paint()
}

// 実fetch失敗（デモ意図とは別）の主表示。サンプルデータは描画しない
function paintFetchError() {
  D.el.innerHTML = '<section class="db"><div class="ct-state is-error"><b class="ct-state__t">データを読み込めませんでした</b><span>保存済みの実データを取得できませんでした。誤認を避けるためサンプルは表示していません。接続を確認して再試行してください</span><button type="button" class="btn" id="db-retry">再試行</button></div></section>'
  const retry = D.el.querySelector('#db-retry'); if (retry) retry.addEventListener('click', () => { renderDashboard(D.el, D.ctx) })
}

// 実データ0件＋デモデータ表示OFF（既定）の主表示。サンプルは描かない。
// 「表示設定」は必ず出す＝OFFにして空になった画面から ON に戻せなくなる袋小路を作らない
function paintNoData() {
  D.el.innerHTML = `<section class="db">
      <div class="db-bar">${displayBtnHtml()}${companyBtnHtml()}</div>
      ${D.dispOpen ? displayPanelHtml() : ''}
      ${D.coOpen ? companyPanelHtml() : ''}
      ${noDataHtml('ダッシュボード')}
    </section>`
  wireDisplay()
  wireCompany()
}

// 「表示設定」ボタン（前提・目標と同じ並び。ON のときは ● を付ける）
function displayBtnHtml() {
  return `<button class="btn" id="db-display">表示設定${isDemoDataOn() ? ' ●' : ''}</button>`
}

// 表示設定パネル（settings/display）。設定の持ち方は settings/labor（乗務員画面の人件費設定）と同じ流儀
function displayPanelHtml() {
  const on = isDemoDataOn()
  return `<div class="ct-panel">
    <h3>表示設定${D.savingDisp ? `<span class="db-savedok">${esc(D.savingDisp)}</span>` : ''}</h3>
    <div class="db-acts" style="margin-top:8px">
      <label class="db-set"><input type="checkbox" id="db-demo-toggle" ${on ? 'checked' : ''} ${D.online ? '' : 'disabled'}><b>デモデータを表示する</b></label>
      ${D.online ? '' : '<span class="db-note">保存は本番のみ（未接続時は常にサンプル表示）</span>'}
    </div>
    <div class="db-acts">
      <span class="db-note" style="color:var(--text)">OFF（既定）＝実データが0件なら「まだありません」と正直に表示します。ON＝同梱サンプル（実CSV由来の架空の実績）で全画面が動き、各画面に「デモデータ表示中」と明示されます。客先デモ用。</span>
    </div>
  </div>`
}

// 切替 → settings/display へ保存 → 再取得して描き直す（同じ判定を通すので画面と設定がずれない）
function wireDisplay() {
  const btn = D.el.querySelector('#db-display')
  // 開閉は再取得なしで描き直す（空表示のときは paint() が使えないので paintNoData 側で）
  if (btn) btn.addEventListener('click', () => { D.dispOpen = !D.dispOpen; D.savingDisp = ''; if (D.trips.length) paint(); else paintNoData() })
  const tg = D.el.querySelector('#db-demo-toggle')
  if (tg) tg.addEventListener('change', async () => {
    const next = tg.checked
    tg.disabled = true
    D.dispOpen = true
    try { await saveDemoSetting(next); D.savingDisp = '' }
    catch (e) { D.savingDisp = '保存失敗（権限/接続）' }
    renderDashboard(D.el, D.ctx)
  })
}

// 「自社情報」ボタン（表示設定の隣）。請求書に刷られる自社ブロックと振込先の正本
function companyBtnHtml() {
  return '<button class="btn" id="db-company">自社情報</button>'
}

// 自社情報パネル（settings/company）。持ち方は settings/display・settings/labor と同じ流儀。
// ここで直した値がそのまま請求書の右上ブロック・振込先2行に出る。
function companyPanelHtml() {
  const c = getCompany()
  const f = (id, label, hint) => `<label>${label}${hint ? `<span class="unit">${esc(hint)}</span>` : ''}<input id="co-${id}" type="text" value="${esc(c[id])}"></label>`
  return `<div class="ct-panel">
    <h3>自社情報（請求書）${D.savingCo ? `<span class="db-savedok">${esc(D.savingCo)}</span>` : ''}</h3>
    <div class="db-af">
      ${f('name', '自社名')}${f('tnum', '登録番号', '適格請求書')}${f('zip', '〒')}${f('address', '住所')}${f('tel', 'TEL')}${f('fax', 'FAX')}
    </div>
    <div class="db-af" style="grid-template-columns:1fr">
      ${f('bank1', '振込先1行目')}${f('bank2', '振込先2行目')}
    </div>
    <div class="db-acts">
      <button class="btn btn--primary" id="co-save"${D.online ? '' : ' disabled'}>保存</button>
      <button class="btn" id="co-reset">初期値に戻す</button>
      <span class="db-note" style="color:var(--text)">請求書の右上（登録番号・自社名・〒・住所・TEL/FAX）と振込先2行に出ます。空欄で保存すると初期値へ戻ります（自社名の無い請求書を出さないため）。${D.online ? '' : '保存は本番のみ。'}</span>
    </div>
  </div>`
}

function wireCompany() {
  const btn = D.el.querySelector('#db-company')
  if (btn) btn.addEventListener('click', () => { D.coOpen = !D.coOpen; D.savingCo = ''; if (D.trips.length) paint(); else paintNoData() })
  const save = D.el.querySelector('#co-save')
  if (save) save.addEventListener('click', async () => {
    const patch = {}
    for (const [k] of COMPANY_FIELDS) { const e2 = D.el.querySelector('#co-' + k); if (e2) patch[k] = e2.value }
    save.disabled = true
    D.coOpen = true
    try { await saveCompany(patch); D.savingCo = '保存しました' }
    catch (e) { D.savingCo = '保存失敗（権限/接続）' }
    if (D.trips.length) paint(); else paintNoData()
  })
  const reset = D.el.querySelector('#co-reset')
  if (reset) reset.addEventListener('click', async () => {
    reset.disabled = true
    D.coOpen = true
    // 全欄を空で渡す＝resolveCompany が既定へ倒す（初期値＝実物の請求書の値）
    const blank = {}; for (const [k] of COMPANY_FIELDS) blank[k] = ''
    try { await saveCompany(blank); D.savingCo = '初期値に戻しました' }
    catch (e) { D.savingCo = '保存失敗（権限/接続）' }
    if (D.trips.length) paint(); else paintNoData()
  })
}

function fleetRef() {
  let km = 0, load = 0, fuel = 0
  for (const t of D.tachos) {
    km += Number(t.runKm != null ? t.runKm : t.km) || 0
    load += Number(t.loadedKm != null ? t.loadedKm : t.loaded) || 0
    fuel += Number(t.fuelLiter != null ? t.fuelLiter : t.fuel) || 0
  }
  if (!km) return null
  const degMonths = new Set(D.tachos.map((t) => monthOf(t, 'sd')).filter(Boolean))
  const ownFare = D.trips.filter((t) => t.kind === 'own' && degMonths.has(monthOf(t, 'dm'))).reduce((a, t) => a + (Number(t.fare) || 0), 0) || 1
  // 燃料・km推定にだけ使う。実車率は当月デジタコが無い月へ持ち越さない。
  return { rate: fuel * 150 / km, kmpy: km / ownFare, kmL: km / fuel }
}

// 月次推移（直近6ヶ月）。選択月と同じ集計パイプラインを月ごとに回すだけ（新規の集計ロジックなし）。
// 前提は保存済みの既定（D.saved）で固定し、試算中の値には追従させない。
function monthTrend(n = 6) {
  const a = D.saved || {}
  return [...D.months].sort().slice(-n).map((m) => {
    const trips = D.trips.filter((t) => monthOf(t, 'dm') === m)
    const tachos = D.tachos.filter((t) => monthOf(t, 'sd') === m)
    const ref = tachos.length ? {} : (fleetRef() || {})
    let mkm = 0, mfuel = 0
    for (const t of tachos) { mkm += Number(t.runKm != null ? t.runKm : t.km) || 0; mfuel += Number(t.fuelLiter != null ? t.fuelLiter : t.fuel) || 0 }
    const kmL = mfuel ? mkm / mfuel : (ref.kmL || 0)
    const opt = { ...ref, names: D.names, dayRate: { large: a.dayLarge, medium: a.dayMedium } }
    if (kmL > 0 && a.dieselYen) opt.rate = a.dieselYen / kmL
    const { summary } = computeOperations(trips, tachos, opt)
    return { m, uri: summary.ji_uri + summary.yo_uri, shushi: summary.zensha }
  })
}

function paint() {
  const el = D.el
  const trips = D.trips.filter((t) => monthOf(t, 'dm') === D.month)
  const tachos = D.tachos.filter((t) => monthOf(t, 'sd') === D.month)
  const ref = tachos.length ? {} : (fleetRef() || {})
  const a = D.assump
  // 当月の燃費(km/L)＝デジタコ実測 or 推定(ref)。前提パネルで上書き可。
  let mkm = 0, mfuel = 0
  for (const t of tachos) { mkm += Number(t.runKm != null ? t.runKm : t.km) || 0; mfuel += Number(t.fuelLiter != null ? t.fuelLiter : t.fuel) || 0 }
  const measKmL = mfuel ? mkm / mfuel : (ref.kmL || 0)
  const effKmL = (a.kmL != null && a.kmL > 0) ? a.kmL : measKmL
  const opt = { ...ref, names: D.names, dayRate: { large: a.dayLarge, medium: a.dayMedium } } // labor は渡さない＝乗務員別率＋試算用率が効く（2026-08-27 一本化）
  if (effKmL > 0) opt.rate = a.dieselYen / effKmL // 燃料 ¥/km＝単価÷燃費（既定でデジタコ実測を再現）
  const dirty = JSON.stringify(a) !== JSON.stringify(D.saved)
  const { summary: s, drivers } = computeOperations(trips, tachos, opt)
  const named = drivers
  const top = [...named].sort((a, b) => b.contrib - a.contrib).slice(0, 5)
  const bot = [...named].sort((a, b) => a.contrib - b.contrib).slice(0, 5)
  const tot_uri = s.ji_uri + s.yo_uri
  // 月次目標（settings/goals）。目標がある指標はカード下部に 目標・達成率・バー を出し、
  // 値本体も 達成=緑/未達=赤 に染める（2026-08-25 仕様）。率系（実車率）は按分しない。
  const mg = (D.goals && D.goals[D.month]) ? D.goals[D.month] : {}
  const G = {
    uri: { goal: mg.uri, pr: goalProgress(tot_uri, mg.uri, D.month, todayIso()), fmt: YEN },
    shushi: { goal: mg.shushi, pr: goalProgress(s.zensha, mg.shushi, D.month, todayIso()), fmt: YEN },
    yo_arari: { goal: mg.yo_arari, pr: goalProgress(s.yo_arari, mg.yo_arari, D.month, todayIso()), fmt: YEN },
    jissharitsu: { goal: mg.jissharitsu, pr: s.estimated ? null : goalProgress(s.jissharitsu * 100, mg.jissharitsu, D.month, todayIso(), { prorate: false }), fmt: (v) => v + '%' },
  }
  // 赤字乗務員（少ないほど良い・目標「◯人以下」）。%は意味を持たないため 達成/超過 表示
  const akajiB = goalBelow(s.akaji, mg.akaji)
  const akajiCls = (fb) => akajiB ? (akajiB.ok ? 'pos' : 'neg') : fb
  const akajiBlock = () => {
    if (!akajiB) return ''
    const c = akajiB.ok ? 'var(--pos)' : 'var(--neg)'
    const t = akajiB.ok ? '達成' : `超過 +${akajiB.over}人`
    return `<div class="ct-kpi__goal"><span>目標 ${mg.akaji}人以下</span><b style="color:${c}">${t}</b></div><div class="ct-kpi__goalbar"><div style="width:100%;background:${c}"></div></div>`
  }
  const gCls = (key, fb) => (G[key] && G[key].pr) ? (G[key].pr.ok ? 'pos' : 'neg') : fb
  const gBlock = (key) => {
    const g = G[key]
    if (!g || !g.pr) return ''
    const c = g.pr.ok ? 'var(--pos)' : 'var(--neg)'
    return `<div class="ct-kpi__goal"><span>目標 ${g.fmt(g.goal)}</span><b style="color:${c}">${g.pr.label} ${fmtGoalPct(g.pr.pct)}</b></div><div class="ct-kpi__goalbar"><div style="width:${g.pr.barPct}%;background:${c}"></div></div>`
  }
  const opts = D.months.map((m) => `<option value="${m}" ${m === D.month ? 'selected' : ''}>${m.replace('-', '年')}月</option>`).join('')
  const sign = (n) => (n >= 0 ? 'pos' : 'neg')

  // 売上構成
  const jiP = pct(s.ji_uri, tot_uri), yoP = Math.round((100 - jiP) * 10) / 10
  // 傭車支払（2026-08-26 決定: 人件費/燃料費/車両経費は下の「全社P&L」ウォーターフォールと
  // 金額が完全重複するため撤去。傭車支払だけはP&L側に単独の行が無い（利用運送粗利へ
  // 相殺済み）ので、ここに残す。単一項目になったため比較用の棒・％は意味を持たず外した）
  const costStat = `<div class="ct-kpi__label">傭車支払</div><div class="ct-kpi__value">${YEN(s.yo_pay)}</div>
      <div class="ct-kpi__sub">利用運送側の費用（全社P&amp;Lでは利用運送粗利へ相殺済み・単独行なし）</div>`

  // 全社P&L ウォーターフォール
  const scale = s.ji_uri || 1
  let run = 0; const steps = []
  steps.push({ l:'自社便売上', s0: 0, e0: s.ji_uri, c: 'var(--text)', v: s.ji_uri }); run = s.ji_uri
  steps.push({ l:'−人件費', s0: run - s.jinken, e0: run, c: 'var(--neg)', v: -s.jinken }); run -= s.jinken
  steps.push({ l:'−燃料費', s0: run - s.fuel, e0: run, c: 'var(--neg)', v: -s.fuel }); run -= s.fuel
  steps.push({ l:'−車両経費', s0: run - s.sharyo, e0: run, c: 'var(--neg)', v: -s.sharyo }); run -= s.sharyo
  steps.push({ l:'＋利用運送粗利', s0: run, e0: run + s.yo_arari, c: 'var(--pos)', v: s.yo_arari }); run += s.yo_arari
  steps.push({ l:'全社収支', s0: 0, e0: run, c: run >= 0 ? 'var(--text)' : 'var(--neg)', v: run })
  const wf = steps.map((st) => {
    const lo = Math.max(0, Math.min(st.s0, st.e0)), hi = Math.max(st.s0, st.e0)
    return `<div class="wfrow"><span class="lab" style="color:var(--text)">${st.l}</span>
      <span class="wftrack"><span style="left:${lo / scale * 100}%;width:${Math.max(0.4, (hi - lo) / scale * 100)}%;background:${st.c}"></span></span>
      <span class="amt ${st.v >= 0 ? 'pos' : 'neg'}">${st.v < 0 ? '−' : ''}${YEN(Math.abs(st.v))}</span></div>`
  }).join('')

  const drow = (d) => `<tr class="ct-row"><td>${esc(d.name)}</td><td class="n">${MAN(d.ji)}</td><td class="n ${sign(d.contrib)}">${YEN(d.contrib)}</td></tr>`
  const driverBody = named.length
    ? `${top.map(drow).join('')}<tr><td colspan="3" style="border:0;height:6px"></td></tr>${bot.map(drow).join('')}`
    : '<tr><td colspan="3" style="border:0"><div class="ct-state is-nomatch"><b class="ct-state__t">この月の自社便はありません</b><span>月を切り替えるか、運行実績を取り込んでください</span></div></td></tr>'

  const ymLabel = D.month ? D.month.replace('-', '年') + '月' : ''
  const csM = D.sheets.filter((h) => monthOf(h) === D.month)
  const nBill = csM.filter((h) => h.kind === 'seikyu').length, nPay = csM.filter((h) => h.kind === 'shiharai').length
  const cfBill = csM.filter((h) => h.kind === 'seikyu').reduce((x, h) => x + (Number(h.sub) || 0), 0)
  const cfPay = csM.filter((h) => h.kind === 'shiharai').reduce((x, h) => x + (Number(h.sub) || 0), 0)
  const dlt = (a, b) => `<td class="n ${a - b < 0 ? 'neg' : 'pos'}">${a - b < 0 ? '−' : ''}${YEN(Math.abs(a - b))}</td>`
  const reconCard = `
        <div class="ct-card c6">
          <h3 class="ct-card__title">確定実績 突合（会計CSV・税抜）${ymLabel ? ` · ${esc(ymLabel)}` : ''}</h3>
          <table class="ct-tbl"><thead><tr><th></th><th class="n">システム計上</th><th class="n">確定実績</th><th class="n">差</th><th class="n">計上率</th></tr></thead>
            <tbody>
              <tr><td>請求（売上）</td><td class="n">${YEN(tot_uri)}</td><td class="n">${YEN(cfBill)}</td>${dlt(tot_uri, cfBill)}<td class="n">${pct(tot_uri, cfBill)}%</td></tr>
              <tr><td>支払（傭車費）</td><td class="n">${YEN(s.yo_pay)}</td><td class="n">${YEN(cfPay)}</td>${dlt(s.yo_pay, cfPay)}<td class="n">${pct(s.yo_pay, cfPay)}%</td></tr>
            </tbody></table>
          <div class="db-note">${csM.length ? `確定実績＝請求${nBill}社・支払${nPay}社の会計CSV（税抜）。計上率＜100%＝システム未計上の便がある可能性（要追加入力）。` : 'この月の会計CSV（お伺い発行履歴）はまだありません。'}</div>
        </div>`
  // 月次推移カード（段1・スミ設計）。乗務員収支の右隣の空白を埋める
  const trend = monthTrend(6)
  const tmax = Math.max(...trend.map((x) => Math.max(x.uri, Math.abs(x.shushi))), 1)
  const MIL = (n) => (n / 1e6).toFixed(1)
  const trendCard = trend.length < 2 ? '' : `
        <div class="ct-card c6">
          <h3 class="ct-card__title">月次推移（総売上 / 全社収支・百万円）</h3>
          <div class="tr-row">${trend.map((x) => {
            const lead = x.m === D.month
            const c1 = lead ? 'var(--signal)' : 'var(--muted)'
            return `<div class="tr-col">
              <div class="tr-bars">
                <span style="height:${Math.max(2, x.uri / tmax * 100)}%;background:${c1};opacity:${lead ? 1 : .55}"></span>
                <span style="height:${Math.max(2, Math.abs(x.shushi) / tmax * 100)}%;background:${x.shushi < 0 ? 'var(--neg)' : c1};opacity:${lead ? .65 : .35}"></span>
              </div>
              <span class="tr-m" style="color:${lead ? 'var(--text)' : 'var(--muted)'}">${x.m.slice(5)}月</span>
              <span class="tr-v">${MIL(x.uri)} / ${MIL(x.shushi)}</span>
            </div>`
          }).join('')}</div>
          <div class="db-note" style="margin-top:10px">濃い棒=総売上、薄い棒=全社収支（赤=マイナス）。選択月のみ発光色。前提は保存済みの既定で集計。</div>
        </div>`

  el.innerHTML = `
    <section class="db">
      <div class="db-bar">
        <select id="db-month">${opts}</select>
        <button class="btn" id="db-assump-toggle">前提（試算）${dirty ? ' ●' : ''}</button>
        <button class="btn" id="db-goal-toggle">目標（月別）${(D.goals && D.goals[D.month] && (D.goals[D.month].uri || D.goals[D.month].shushi || D.goals[D.month].yo_arari || D.goals[D.month].jissharitsu || D.goals[D.month].akaji != null)) ? ' ●' : ''}</button>
        ${displayBtnHtml()}
        ${companyBtnHtml()}
        ${D.isSample ? `<span class="db-demo">${DEMO_TAG}</span>` : ''}
        ${s.estimated ? '<span class="db-demo" style="background:var(--head);color:var(--text);border-color:var(--e1)">推定：この月はデジタコ未取込。燃料・粗利は他月の実車実績から推定</span>' : ''}
        <button type="button" class="btn db-open" data-prototype="dashboard">詳細プロトタイプ ↗</button>
      </div>
      ${D.dispOpen ? displayPanelHtml() : ''}
      ${D.coOpen ? companyPanelHtml() : ''}
      ${D.open ? assumpPanel(a, effKmL, measKmL, dirty) : ''}
      ${D.goalOpen ? goalPanel() : ''}
      <div class="ct-kpis">
        <div class="ct-kpi ${gCls('uri', 'acc')}"><div class="ct-kpi__label">総売上（請求ベース）</div><div class="ct-kpi__value">${YEN(tot_uri)}</div><div class="ct-kpi__sub">${esc(ymLabel)} · 自社便＋利用運送</div>${gBlock('uri')}</div>
        <div class="ct-kpi ${gCls('shushi', sign(s.zensha))}"><div class="ct-kpi__label">全社収支</div><div class="ct-kpi__value">${YEN(s.zensha)}</div><div class="ct-kpi__sub">収支率 ${pct(s.zensha, tot_uri)}%（車両経費後）</div>${gBlock('shushi')}</div>
        <div class="ct-kpi ${gCls('yo_arari', '')}"><div class="ct-kpi__label">利用運送 粗利</div><div class="ct-kpi__value">${YEN(s.yo_arari)}</div><div class="ct-kpi__sub">傭車 ${s.you_runs}件</div>${gBlock('yo_arari')}</div>
        <div class="ct-kpi ${gCls('jissharitsu', '')}"><div class="ct-kpi__label">実車率</div><div class="ct-kpi__value">${s.estimated ? '—' : ((s.jissharitsu * 100).toFixed(1) + '%')}</div><div class="ct-kpi__sub">${s.estimated ? 'デジタコ未取込（当月）' : '稼働 ' + s.unit_plates + '台・' + (s.match_rate * 100).toFixed(0) + '%突合'}</div>${gBlock('jissharitsu')}</div>
        <div class="ct-kpi ${akajiCls(s.akaji ? 'neg' : '')}"><div class="ct-kpi__label">赤字乗務員</div><div class="ct-kpi__value">${s.akaji}<span style="font-size:12px"> /${s.drivers}</span></div><div class="ct-kpi__sub">粗利益＜0</div>${akajiBlock()}</div>
      </div>
      <div class="ct-grid">
        ${reconCard}
        <div class="ct-card c6">
          <h3 class="ct-card__title">売上構成（自社便 / 利用運送）</h3>
          <div class="stack"><span style="width:${jiP}%;background:var(--accent)"></span><span style="width:${yoP}%;background:var(--pos)"></span></div>
          <div class="lg"><span><i style="background:var(--accent)"></i>自社便 ${YEN(s.ji_uri)}（${jiP}%）</span><span><i style="background:var(--pos)"></i>利用運送 ${YEN(s.yo_uri)}（${yoP}%）</span></div>
        </div>
        <div class="ct-card c6 auto-h">
          <h3 class="ct-card__title">コスト構成</h3>
          ${costStat}
        </div>
        <div class="ct-card c6">
          <h3 class="ct-card__title">全社P&amp;L（売上 → コスト → 収支）</h3>
          <div class="wf">${wf}</div>
        </div>
        <div class="ct-card c6">
          <h3 class="ct-card__title">乗務員 収支（上位 / 下位）</h3>
          <table class="ct-tbl"><thead><tr><th>乗務員</th><th class="n">自社便売上</th><th class="n">粗利益</th></tr></thead>
            <tbody>${driverBody}</tbody></table>
        </div>
        ${trendCard}
      </div>
      <div class="db-note">全社KPIは選択月の trips × デジタコ tachographs を集約（運行実績×収支と同一ロジック）。人件費率＝乗務員別（個別設定が無い分は試算用${(s.laborRate * 100).toFixed(1)}%・乗務員画面で設定）・燃料¥${a.dieselYen}/L（燃費${(effKmL || 0).toFixed(2)}km/L）・車両日額 大型¥${Number(a.dayLarge).toLocaleString('ja-JP')}/中型¥${Number(a.dayMedium).toLocaleString('ja-JP')}${dirty ? '（試算中）' : ''}。前提は「前提（試算）」で変更・保存できます。${s.estimated ? '<br><b style="color:var(--text)">※この月はデジタコ未取込のため、実車率は表示せず、燃料・粗利は他月のフリート実績からの推定値です。</b>' : ''}</div>
    </section>`
  const monthSel = el.querySelector('#db-month')
  if (monthSel) monthSel.addEventListener('change', (e) => { D.month = setSelectedMonth(e.target.value) || e.target.value; paint() })
  el.querySelector('#db-assump-toggle').addEventListener('click', () => { D.open = !D.open; paint() })
  el.querySelector('#db-goal-toggle').addEventListener('click', () => { D.goalOpen = !D.goalOpen; paint() })
  wireDisplay()
  wireCompany()
  if (D.goalOpen) {
    const cur = { ...((D.goals || {})[D.month] || {}) }
    const MONEY = new Set(['uri', 'shushi', 'yo_arari'])
    const gbind = (id, key) => { const e2 = el.querySelector(id); if (e2) e2.addEventListener('change', () => {
      if (MONEY.has(key)) { const n = parseNum(e2.value); cur[key] = n; e2.value = fmtNum(n) }
      else { const n = Number(e2.value); cur[key] = (e2.value === '' || isNaN(n)) ? null : n }
      D.goalDraft = cur
    }) }
    gbind('#g-uri', 'uri'); gbind('#g-shushi', 'shushi'); gbind('#g-yo', 'yo_arari'); gbind('#g-jis', 'jissharitsu'); gbind('#g-akaji', 'akaji')
    D.goalDraft = cur
    const gs = el.querySelector('#g-save'); if (gs) gs.addEventListener('click', saveGoals)
  }
  if (D.open) {
    const bind = (id, f) => { const e2 = el.querySelector(id); if (e2) e2.addEventListener('change', () => { f(e2.value); paint() }) }
    bind('#a-large', (v) => { D.assump.dayLarge = Math.max(0, parseNum(v) || 0) })
    bind('#a-medium', (v) => { D.assump.dayMedium = Math.max(0, parseNum(v) || 0) })
    bind('#a-diesel', (v) => { D.assump.dieselYen = Math.max(0, parseNum(v) || 0) })
    bind('#a-kml', (v) => { const n = Number(v); D.assump.kmL = (n > 0 ? n : null) }) // 実績側（KPI・収支）
    // 試算側（受注試算の燃料費）。生値で持ち、読む側 normQuoteFuel が既定へ倒す
    bind('#a-qkml-m', (v) => { const n = Number(v); D.assump.quoteKmLMedium = (n > 0 ? n : null) })
    bind('#a-qkml-l', (v) => { const n = Number(v); D.assump.quoteKmLLarge = (n > 0 ? n : null) })
    const sv = el.querySelector('#a-save'); if (sv) sv.addEventListener('click', saveAssump)
    const rs = el.querySelector('#a-reset'); if (rs) rs.addEventListener('click', () => { D.assump = { ...D.saved }; paint() })
  }
}

// 目標（月別）パネル。選択中の月の3指標の目標額を入力（空欄=目標なし）。
function goalPanel() {
  const g = (D.goals || {})[D.month] || {}
  const v = (x) => (x == null || x === '' ? '' : x)
  const badge = D.savingGoal ? `<span class="db-savedok">${esc(D.savingGoal)}</span>` : ''
  return `<div class="ct-panel">
    <h3>目標（${esc(D.month ? D.month.replace('-', '年') + '月' : '')}）${badge}</h3>
    <div class="db-af">
      <label>総売上 目標<span class="unit">円（自社＋利用運送）</span><input id="g-uri" type="text" inputmode="numeric" value="${fmtNum(g.uri)}" placeholder="未設定"></label>
      <label>全社収支 目標<span class="unit">円</span><input id="g-shushi" type="text" inputmode="numeric" value="${fmtNum(g.shushi)}" placeholder="未設定"></label>
      <label>利用運送粗利 目標<span class="unit">円</span><input id="g-yo" type="text" inputmode="numeric" value="${fmtNum(g.yo_arari)}" placeholder="未設定"></label>
      <label>実車率 目標<span class="unit">%（按分なし）</span><input id="g-jis" type="number" step="0.1" min="0" max="100" value="${v(g.jissharitsu)}" placeholder="未設定"></label>
      <label>赤字乗務員 目標<span class="unit">人以下（0可）</span><input id="g-akaji" type="number" step="1" min="0" value="${v(g.akaji)}" placeholder="未設定"></label>
    </div>
    <div class="db-acts">
      <button class="btn btn--primary" id="g-save">この月の目標を保存</button>
      <span class="db-note" style="color:var(--text)">締まった月は素の達成率、進行中の月は日割り按分の「ペース」で表示。空欄にして保存すると目標なしに戻ります。</span>
    </div>
  </div>`
}

async function saveGoals() {
  if (!D.online) {
    // デモ: この画面のメモリ上にだけ反映（再読み込みで消える）。挙動確認用
    const cur = { ...(D.goalDraft || {}) }
    for (const k of Object.keys(cur)) { if (cur[k] == null || cur[k] === '' || isNaN(Number(cur[k]))) delete cur[k] }
    const months = { ...(D.goals || {}) }
    if (Object.keys(cur).length) months[D.month] = cur; else delete months[D.month]
    D.goals = months
    D.savingGoal = 'デモ：画面にのみ反映（保存は本番）'
    paint(); setTimeout(() => { D.savingGoal = ''; paint() }, 2200); return
  }
  try {
    D.savingGoal = '保存中…'; paint()
    const cur = { ...(D.goalDraft || {}) }
    for (const k of Object.keys(cur)) { if (cur[k] == null || cur[k] === '' || isNaN(Number(cur[k]))) delete cur[k] }
    const months = { ...(D.goals || {}) }
    if (Object.keys(cur).length) months[D.month] = cur; else delete months[D.month]
    await upsert('settings', 'goals', { months, updatedAt: new Date().toISOString() })
    D.goals = months
    D.savingGoal = '保存しました ✓'
  } catch (e) { D.savingGoal = '保存失敗（権限/接続）' }
  paint(); setTimeout(() => { D.savingGoal = ''; paint() }, 2000)
}

function assumpPanel(a, effKmL, measKmL, dirty) {
  const r = (n) => Math.round(Number(n) * 100) / 100
  const badge = dirty ? '<span class="db-dirty">試算中（未保存）</span>' : (D.saving ? `<span class="db-savedok">${esc(D.saving)}</span>` : '')
  return `<div class="ct-panel">
    <h3>前提（試算）${badge}</h3>
    <div class="db-af">
      <label>車両日額・大型<span class="unit">円/稼働日</span><input id="a-large" type="text" inputmode="numeric" value="${fmtNum(a.dayLarge)}"></label>
      <label>車両日額・中型<span class="unit">円/稼働日</span><input id="a-medium" type="text" inputmode="numeric" value="${fmtNum(a.dayMedium)}"></label>
      <label>燃料単価<span class="unit">円/L</span><input id="a-diesel" type="text" inputmode="numeric" value="${fmtNum(a.dieselYen)}"></label>
      <label>実績の燃費<span class="unit">km/L（空欄=実測${r(measKmL)}）</span><input id="a-kml" type="number" step="0.1" min="0" value="${a.kmL != null ? a.kmL : r(effKmL)}" placeholder="${r(measKmL)}"></label>
      <label>試算の燃費・中型<span class="unit">km/L（既定${QUOTE_FUEL_DEFAULTS.medium}）</span><input id="a-qkml-m" type="number" step="0.1" min="0" value="${a.quoteKmLMedium != null ? a.quoteKmLMedium : QUOTE_FUEL_DEFAULTS.medium}" placeholder="${QUOTE_FUEL_DEFAULTS.medium}"></label>
      <label>試算の燃費・大型<span class="unit">km/L（既定${QUOTE_FUEL_DEFAULTS.large}）</span><input id="a-qkml-l" type="number" step="0.1" min="0" value="${a.quoteKmLLarge != null ? a.quoteKmLLarge : QUOTE_FUEL_DEFAULTS.large}" placeholder="${QUOTE_FUEL_DEFAULTS.large}"></label>
    </div>
    <div class="db-acts">
      <button class="btn btn--primary" id="a-save">この値を既定として保存</button>
      <button class="btn" id="a-reset">既定に戻す</button>
      <span class="db-note" style="color:var(--text)">変更でKPI・P&Lが即再計算。保存で次回以降の既定に。</span>
    </div>
    <div class="db-acts">
      <span class="db-note" style="color:var(--text)">燃費は役割が2つあります。<b>実績の燃費</b>＝この画面のKPI・収支で使う値（デジタコ実測が正。未取込の月だけ上書き）。<b>試算の燃費</b>＝配車・受注の試算パネルで燃料費を出す前提（距離÷燃費×燃料単価）。車格で倍近く違うため中型・大型に分けています。<b>試算の燃費を変えても実績のKPI・収支は動きません。</b></span>
    </div>
  </div>`
}

async function saveAssump() {
  if (!D.online) { D.saving = 'デモ：保存は本番のみ'; paint(); setTimeout(() => { D.saving = ''; paint() }, 1800); return }
  try {
    D.saving = '保存中…'; paint()
    const a = D.assump
    await upsert('settings', 'assumptions', { dayLarge: a.dayLarge, dayMedium: a.dayMedium, dieselYen: a.dieselYen,
      kmL: (a.kmL != null ? a.kmL : null),                                       // 実績の燃費（KPI・収支）
      quoteKmLMedium: (a.quoteKmLMedium != null ? a.quoteKmLMedium : null),      // 試算の燃費・中型（受注試算のみ）
      quoteKmLLarge: (a.quoteKmLLarge != null ? a.quoteKmLLarge : null),         // 試算の燃費・大型（受注試算のみ）
      updatedAt: new Date().toISOString() }) // labor は書かない（#4 一本化）
    await refreshQuoteFuel(true) // 保存直後に試算側ストアへ反映＝再読み込みなしで受注試算に効く
    D.saved = { ...a }; D.saving = '保存しました ✓'
  } catch (e) { D.saving = '保存失敗（権限/接続）' }
  paint(); setTimeout(() => { D.saving = ''; paint() }, 2000)
}
